import {
  Component,
  OnDestroy,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatRipple } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import {
  FuseHorizontalNavigationComponent,
  FuseNavigationService,
  FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { WebColorScheme } from '@libs/shared/api';
import { ColorSchemeMenuButtonComponent } from '@libs/shared/ui/color-scheme-menu-button/color-scheme-menu-button.component';
import { ExpertWorkspaceLogoBlockComponent } from '@libs/shared/ui/expert-workspace-logo-block/expert-workspace-logo-block.component';
import { ExpertWorkspaceImageFileKeyPipe } from '@libs/shared/utils/pipe/expert-workspace-image-file-key.pipe';
import { NavigationService } from '@sprint-expert/app/core/navigation/navigation.service';
import { Navigation } from '@sprint-expert/app/core/navigation/navigation.types';
import { injectCoreFeature } from '@sprint-expert/app/core/state/core.service';
import { UserComponent } from '@sprint-expert/app/layout/common/user/user.component';
import { ExpertEventNotificationComponent } from '@sprint-expert/app/modules/expert-event-notification/expert-event-notification.component';
import { ExpertWorkspaceReferralInvitationDeepLinkDetailDialogTriggerDirective } from '@sprint-expert/app/modules/expert-workspace-referral-invitation-deep-link-detail/expert-workspace-referral-invitation-deep-link-detail-dialog-trigger.directive';
import { PromotionBannerComponent } from '@sprint-expert/app/modules/promotion-banner/promotion-banner.component';
import { Subject, takeUntil } from 'rxjs';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'expert-workspace-with-navigation-layout',
  templateUrl: './expert-workspace-with-navigation-layout.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    FuseVerticalNavigationComponent,
    FuseHorizontalNavigationComponent,
    MatIconModule,
    UserComponent,
    RouterOutlet,
    ColorSchemeMenuButtonComponent,
    MatIconButton,
    ExpertWorkspaceImageFileKeyPipe,
    MatRipple,
    ExpertWorkspaceLogoBlockComponent,
    ExpertEventNotificationComponent,
    MatButton,
    ExpertWorkspaceReferralInvitationDeepLinkDetailDialogTriggerDirective,
    PromotionBannerComponent,
    PromotionBannerComponent,
  ],
})
export class ExpertWorkspaceWithNavigationLayoutComponent
  implements OnInit, OnDestroy
{
  isScreenMedium$$ = signal(false);
  navigation: Navigation;
  readonly core = injectCoreFeature();

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  /**
   * Constructor
   */
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _navigationService: NavigationService,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fuseNavigationService: FuseNavigationService
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to navigation data
    this._navigationService.navigation$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((navigation: Navigation) => {
        this.navigation = navigation;
      });

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        this.isScreenMedium$$.set(!matchingAliases.includes('md'));
      });

    this.core.fetchExpertEventNotificationUnreadCount();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
        name
      );

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  setWebColorScheme(webColorScheme: WebColorScheme): void {
    this.core.updateWebColorScheme(webColorScheme);
  }

  navigateToExpertWorkspaceSelection() {
    this._router.navigateByUrl('expert-workspace-selection');
  }
}
